<template>
  <div class="col-xl-4 col-lg-6 col-md-12 box-col-50 cols-xl-50">
    <div class="card product-slider">
      <div class="card-header pb-0">
        <div class="d-flex justify-content-between">
          <div class="flex-grow-1">
            <p class="square-after f-w-600">
              Sección Destacada de Productos en Oferta<i class="fa fa-circle"> </i>
            </p>
            <h4>{{ sectionName || 'Productos' }}</h4>
          </div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Cargando...</span>
          </div>
        </div>
        <div v-else-if="error" class="text-center text-muted py-4">
          <p>{{ error }}</p>
        </div>
        <div v-else-if="!products.length" class="text-center text-muted py-5">
          <p>No hay productos disponibles</p>
        </div>
        <div v-else class="product-slider-wrapper">
          <div class="owl-carousel owl-theme" id="owl-carousel-1">
            <swiper :space-between="20" :pagination="{ clickable: true }" :modules="modules">
              <swiper-slide v-for="product in products" :key="getProductId(product)">
                <div class="item">
                  <img
                    class="img-fluid"
                    :src="getProductImage(product)"
                    :alt="getProductDescription(product)"
                    @error="handleImageError"
                  />
                  <div class="product-content">
                    <div v-if="isOnSale(product)" class="badge badge-primary">En Oferta</div>
                    <h4>
                      <router-link to="/inventory">{{
                        getProductDescription(product)
                      }}</router-link>
                      <i class="fa fa-check-circle"></i>
                    </h4>
                    <div class="price-info">
                      <h5 class="f-16" :class="{ 'text-primary': isOnSale(product) }">
                        {{ formatPrice(product) }}
                      </h5>
                      <span
                        v-if="isOnSale(product)"
                        class="text-muted text-decoration-line-through small ms-2"
                      >
                        {{ formatRegularPrice(product) }}
                      </span>
                    </div>
                    <div v-if="getOfferType(product)" class="mt-2">
                      <span
                        class="badge"
                        :class="getOfferTypeBadgeClass(product)"
                        style="font-size: 0.65rem; padding: 3px 8px"
                      >
                        {{ getOfferType(product) }}
                      </span>
                    </div>
                  </div>
                </div>
              </swiper-slide>
            </swiper>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getHomeSections } from '@/services/homeSectionsService'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'
import 'swiper/css/pagination'
import { Pagination } from 'swiper'

// Cache configuration
const CACHE_KEY = 'dashboard_section_products'
const CACHE_DURATION = 60 * 60 * 1000 // 1 hora en milisegundos

export default {
  name: 'ProductSlider',
  components: {
    Swiper,
    SwiperSlide,
  },
  setup() {
    return {
      modules: [Pagination],
    }
  },
  data() {
    return {
      products: [],
      loading: false,
      error: null,
      sectionName: null,
    }
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),

    /**
     * Get the store code to use for prices and inventory
     * If defaultStore is 0 or null, use 7127, otherwise use defaultStore
     */
    storeCode() {
      if (!this.currentUser || !this.currentUser.defaultStore) {
        return '7127'
      }

      const defaultStore = this.currentUser.defaultStore
      const storeNum = Number(defaultStore)

      // If defaultStore is 0, '0', null, undefined, empty string, or NaN, use 7127
      if (defaultStore === 0 || defaultStore === '0' || storeNum === 0 || isNaN(storeNum)) {
        return '7127'
      }

      return String(defaultStore)
    },
  },
  watch: {
    // Watch for user changes to reload products if store changes
    currentUser: {
      handler(newUser, oldUser) {
        if (newUser && oldUser && newUser.defaultStore !== oldUser.defaultStore) {
          // Clear cache and reload if store changed
          localStorage.removeItem(CACHE_KEY)
          this.loadProducts()
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.loadProducts()
  },
  methods: {
    /**
     * Load products from the section with lowest order
     */
    async loadProducts() {
      // Check cache first
      const cached = this.getCachedProducts()
      if (cached) {
        this.products = cached.products || []
        this.sectionName = cached.sectionName || null
        return
      }

      this.loading = true
      this.error = null

      try {
        // Get all home sections
        const sectionsResult = await getHomeSections()

        if (!sectionsResult.success || !sectionsResult.data || sectionsResult.data.length === 0) {
          this.error = 'No hay secciones disponibles'
          this.products = []
          this.loading = false
          return
        }

        // Filter only active sections and sort by order (ascending)
        const activeSections = sectionsResult.data
          .filter(section => section.activa !== false)
          .sort((a, b) => (a.orden || 0) - (b.orden || 0))

        if (activeSections.length === 0) {
          this.error = 'No hay secciones activas disponibles'
          this.products = []
          this.loading = false
          return
        }

        // Get the section with the lowest order (first in sorted array)
        const lowestOrderSection = activeSections[0]
        this.sectionName = lowestOrderSection.nombre || 'Productos'

        // Extract products from productos object
        const productos = lowestOrderSection.productos || {}

        // Convert productos object to array
        // Products are stored as: productos[CODIGO] = { full product object }
        this.products = Object.keys(productos).map(codigo => {
          const productData = productos[codigo]
          // Handle different possible structures
          if (productData.Data) {
            // If product is stored as { Data: {...}, ProductoID: ... }
            return {
              CODIGO: codigo,
              ...productData.Data,
            }
          } else {
            // If product is stored directly
            return {
              CODIGO: codigo,
              ...productData,
            }
          }
        })

        // Debug: Log first product to see its structure
        if (this.products.length > 0) {
          console.log('ProductSlider - First product sample:', this.products[0])
          console.log('ProductSlider - Image URLs:', {
            URLS512: this.products[0].URLS512,
            URLS256: this.products[0].URLS256,
            URLS128: this.products[0].URLS128,
          })
        }

        // Save to cache
        this.setCachedProducts({
          products: this.products,
          sectionName: this.sectionName,
        })
      } catch (error) {
        console.error('Error loading section products:', error)
        this.error = 'Error al cargar productos'
        this.products = []
      } finally {
        this.loading = false
      }
    },

    /**
     * Get cached products if still valid
     */
    getCachedProducts() {
      try {
        const cached = localStorage.getItem(CACHE_KEY)
        if (!cached) return null

        const { data, timestamp } = JSON.parse(cached)
        const now = Date.now()

        // Check if cache is still valid (less than 1 hour old)
        if (now - timestamp < CACHE_DURATION) {
          return data
        }

        // Cache expired, remove it
        localStorage.removeItem(CACHE_KEY)
        return null
      } catch (error) {
        console.error('Error reading cache:', error)
        return null
      }
    },

    /**
     * Save products to cache
     */
    setCachedProducts(cacheData) {
      try {
        const cachePayload = {
          data: cacheData,
          timestamp: Date.now(),
        }
        localStorage.setItem(CACHE_KEY, JSON.stringify(cachePayload))
      } catch (error) {
        console.error('Error saving cache:', error)
      }
    },

    /**
     * Get product ID
     */
    getProductId(product) {
      return product.CODIGO || product.objectID || product.ProductoID || 'unknown'
    },

    /**
     * Get product description
     */
    getProductDescription(product) {
      return product.DESCRIPCION || 'Producto sin descripción'
    },

    /**
     * Get product image URL
     * Prioridad: URLS512 > URLS256 > URLS128
     */
    getProductImage(product) {
      if (!product) {
        return ''
      }

      // Fallback to URLS256
      if (product.URLS256 && product.URLS256.trim()) {
        return product.URLS256
      }

      // Return empty string if no image found
      return ''
    },

    /**
     * Format price with currency (shows offer price if on sale, regular price otherwise)
     */
    formatPrice(product) {
      if (!product) return '$0.00'
      let price = 0

      if (this.isOnSale(product)) {
        // Use offer price if on sale
        price = this.getStoreValue(product, `PRECIOOFERTA_${this.storeCode}`, 0)
      } else {
        // Use regular price
        price = this.getStoreValue(product, `PRECIO_${this.storeCode}`, 0)
      }

      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(Number(price) || 0)
    },

    /**
     * Format regular price (always shows the regular price, even if on sale)
     */
    formatRegularPrice(product) {
      if (!product) return '$0.00'
      const price = this.getStoreValue(product, `PRECIO_${this.storeCode}`, 0)

      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(Number(price) || 0)
    },

    /**
     * Get offer type label
     * 101 = "Descuento directo"
     * 103 = "Abono a tarjeta club"
     */
    getOfferType(product) {
      if (!this.isOnSale(product)) {
        return null
      }

      const tipoOferta = this.getStoreValue(product, `TIPOOFERTA_${this.storeCode}`, 0)

      if (tipoOferta === 101) {
        return 'Descuento directo'
      } else if (tipoOferta === 103) {
        return 'Abono a tarjeta club'
      }

      return null
    },

    /**
     * Get badge class for offer type
     */
    getOfferTypeBadgeClass(product) {
      const tipoOferta = this.getStoreValue(product, `TIPOOFERTA_${this.storeCode}`, 0)

      if (tipoOferta === 101) {
        return 'badge-primary'
      } else if (tipoOferta === 103) {
        return 'badge-info'
      }

      return 'badge-secondary'
    },

    /**
     * Check if product is on sale
     * Conditions:
     * 1. OFERTA_$storeCode = 1
     * 2. Current timestamp is between INICIOOFERTA and FINOFERTA
     * 3. PRECIOOFERTA < PRECIO
     */
    isOnSale(product) {
      const storeCode = this.storeCode
      const oferta = this.getStoreValue(product, `OFERTA_${storeCode}`, 0)

      if (oferta !== 1) {
        return false
      }

      const precioOferta = this.getStoreValue(product, `PRECIOOFERTA_${storeCode}`, 0)
      const precio = this.getStoreValue(product, `PRECIO_${storeCode}`, 0)

      // Check if offer price is less than regular price
      if (precioOferta >= precio || precioOferta <= 0) {
        return false
      }

      // Check if current time is within offer period
      const inicioOferta = this.getStoreValue(product, `INICIOOFERTA_${storeCode}`, 0)
      const finOferta = this.getStoreValue(product, `FINOFERTA_${storeCode}`, 0)

      if (!inicioOferta || !finOferta) {
        return false
      }

      const now = Date.now()
      return now >= inicioOferta && now <= finOferta
    },

    /**
     * Get store-specific value from product
     */
    getStoreValue(product, key, defaultValue = 0) {
      if (!product || !key) return defaultValue

      let value = product[key]

      // If value is undefined or null, return default
      if (value === undefined || value === null) {
        return defaultValue
      }

      // Convert numeric strings to numbers
      if (typeof value === 'string' && value.trim() !== '' && !isNaN(value)) {
        value = Number(value)
      }

      return value
    },

    /**
     * Handle image loading errors
     * Try fallback image sizes if main image fails
     */
    handleImageError(event) {
      const img = event.target
      const currentSrc = img.src

      // Try different image sizes as fallback
      if (currentSrc.includes('URLS512') || currentSrc.includes('512')) {
        // If URLS512 failed, try URLS256
        const newSrc = currentSrc.replace('512', '256')
        if (newSrc !== currentSrc) {
          img.src = newSrc
          return
        }
      }

      if (currentSrc.includes('URLS256') || currentSrc.includes('256')) {
        // If URLS256 failed, try URLS128
        const newSrc = currentSrc.replace('256', '128')
        if (newSrc !== currentSrc) {
          img.src = newSrc
          return
        }
      }

      if (currentSrc.includes('URLS128') || currentSrc.includes('128')) {
        // If URLS128 also failed, try to extract base URL and find alternative
        // Hide image if all attempts failed
        img.style.display = 'none'
        return
      }

      // If all attempts failed, hide the image
      img.style.display = 'none'
    },
  },
}
</script>
